import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  IconButton,
  useToast,
  Badge,
  Collapse,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FaMicrophone,
  FaStop,
  FaKeyboard,
  FaPaperPlane,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { useSession } from "@gadgetinc/react";
import { useGlobalAction } from "@gadgetinc/react";
import { api } from "../api";
import { hasAnyRole } from "../lib/roleUtils";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface PatientContext {
  age?: number;
  sex?: string;
  chiefComplaint?: string;
  heartRate?: number;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  respiratoryRate?: number;
  spO2?: number;
  temperature?: number;
  bloodGlucose?: number;
}

type RecordingState = "idle" | "recording" | "processing" | "responding";
type Mode = "voice" | "text";

export default function MedicalControl() {
  const navigate = useNavigate();
  const session = useSession(api);
  const toast = useToast();
  const { isOpen: isPanelOpen, onToggle: togglePanel } = useDisclosure({ defaultIsOpen: true });

  // State management
  const [mode, setMode] = useState<Mode>("voice");
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  const [textInput, setTextInput] = useState("");
  const [patientContext, setPatientContext] = useState<PatientContext>({});

  // Audio recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);

  // API hook
  const [{ data, error, fetching }, callMedicalControl] = useGlobalAction(api.callMedicalControl);

  // Check authorization
  useEffect(() => {
    if (session && !hasAnyRole(session.user?.roles as any, ["admin"])) {
      toast({
        title: "Access Denied",
        description: "Admin role required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      navigate("/signed-in");
    }
  }, [session, navigate, toast]);

  // Auto-scroll conversation
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationHistory]);

  // Load conversation from sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem("medicalControlConversation");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversationHistory(
          parsed.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }))
        );
      } catch (e) {
        console.error("Failed to load conversation history", e);
      }
    }
  }, []);

  // Save conversation to sessionStorage
  useEffect(() => {
    if (conversationHistory.length > 0) {
      sessionStorage.setItem("medicalControlConversation", JSON.stringify(conversationHistory));
    }
  }, [conversationHistory]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (mode === "voice" && e.code === "Space" && !e.repeat) {
        e.preventDefault();
        if (recordingState === "idle") {
          startRecording();
        } else if (recordingState === "recording") {
          stopRecording();
        }
      }
      if (e.code === "Escape" && recordingState === "recording") {
        cancelRecording();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode, recordingState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        await processRecording();
      };

      mediaRecorder.start();
      setRecordingState("recording");

      // 2 minute timeout
      recordingTimeoutRef.current = setTimeout(() => {
        if (mediaRecorder.state === "recording") {
          mediaRecorder.stop();
          toast({
            title: "Recording timeout",
            description: "Maximum recording length reached (2 minutes)",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      }, 120000);
    } catch (err) {
      console.error("Error starting recording:", err);
      toast({
        title: "Microphone Error",
        description: "Could not access microphone. Please check permissions.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setRecordingState("idle");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      audioChunksRef.current = [];
      setRecordingState("idle");
      toast({
        title: "Recording cancelled",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const processRecording = async () => {
    if (audioChunksRef.current.length === 0) {
      setRecordingState("idle");
      return;
    }

    setRecordingState("processing");

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      
      // Transcribe audio using backend
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      const transcriptionResponse = await fetch("/api/transcribe-audio", {
        method: "POST",
        body: formData,
      });

      if (!transcriptionResponse.ok) {
        throw new Error("Transcription failed");
      }

      const { transcription } = await transcriptionResponse.json();

      if (!transcription || transcription.trim().length === 0) {
        throw new Error("No speech detected");
      }

      // Add user message to history
      addMessage("user", transcription);

      // Send to medical control
      await sendToMedicalControl(transcription);
    } catch (err) {
      console.error("Error processing recording:", err);
      toast({
        title: "Processing Error",
        description: err instanceof Error ? err.message : "Failed to process recording",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setRecordingState("idle");
    }
  };

  const sendToMedicalControl = async (message: string) => {
    setRecordingState("responding");

    try {
      const response = await callMedicalControl({
        message,
        patientContext: Object.keys(patientContext).length > 0 ? patientContext : undefined,
        conversationHistory: conversationHistory.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
      });

      if (error) {
        throw new Error("Medical control request failed");
      }

      // Add assistant response to history
      if (response && response.response) {
        addMessage("assistant", response.response);
      }
    } catch (err) {
      console.error("Error calling medical control:", err);
      toast({
        title: "Communication Error",
        description: "Failed to reach medical control",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRecordingState("idle");
    }
  };

  const handleTextSubmit = async () => {
    if (!textInput.trim()) return;

    const message = textInput.trim();
    setTextInput("");
    addMessage("user", message);

    await sendToMedicalControl(message);
  };

  const addMessage = (role: "user" | "assistant", content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      role,
      content,
      timestamp: new Date(),
    };
    setConversationHistory((prev) => [...prev, newMessage]);
  };

  const getStatusText = () => {
    switch (recordingState) {
      case "recording":
        return "Recording...";
      case "processing":
        return "Transcribing...";
      case "responding":
        return "Waiting for Medical Control...";
      default:
        return "Ready";
    }
  };

  const getStatusColor = () => {
    switch (recordingState) {
      case "recording":
        return "red";
      case "processing":
      case "responding":
        return "yellow";
      default:
        return "green";
    }
  };

  if (!session) {
    return (
      <Flex h="100vh" bg="gray.900" align="center" justify="center">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box h="100vh" bg="gray.900" color="white" overflow="hidden">
      {/* Header */}
      <Flex
        as="header"
        bg="gray.800"
        px={6}
        py={4}
        align="center"
        justify="space-between"
        borderBottom="1px"
        borderColor="gray.700"
      >
        <Heading size="lg">Medical Control Consultation</Heading>
        <HStack spacing={4}>
          <Badge colorScheme={getStatusColor()} fontSize="md" px={3} py={1}>
            {getStatusText()}
          </Badge>
          <Button
            leftIcon={mode === "voice" ? <FaKeyboard /> : <FaMicrophone />}
            onClick={() => setMode(mode === "voice" ? "text" : "voice")}
            colorScheme="blue"
            size="sm"
            isDisabled={recordingState !== "idle"}
          >
            {mode === "voice" ? "Text Mode" : "Voice Mode"}
          </Button>
        </HStack>
      </Flex>

      {/* Main Content */}
      <Flex h="calc(100vh - 73px)" overflow="hidden">
        {/* Side Panel */}
        <Box
          w={isPanelOpen ? "300px" : "0"}
          bg="gray.800"
          borderRight="1px"
          borderColor="gray.700"
          overflow="hidden"
          transition="width 0.3s"
        >
          <Collapse in={isPanelOpen}>
            <Box p={4} overflowY="auto" h="full">
              <Heading size="sm" mb={4}>
                Patient Context
              </Heading>
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Age</FormLabel>
                  <NumberInput
                    value={patientContext.age || ""}
                    onChange={(_, val) => setPatientContext({ ...patientContext, age: val })}
                    size="sm"
                  >
                    <NumberInputField bg="gray.700" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Sex</FormLabel>
                  <Select
                    value={patientContext.sex || ""}
                    onChange={(e) => setPatientContext({ ...patientContext, sex: e.target.value })}
                    size="sm"
                    bg="gray.700"
                  >
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Chief Complaint</FormLabel>
                  <Input
                    value={patientContext.chiefComplaint || ""}
                    onChange={(e) =>
                      setPatientContext({ ...patientContext, chiefComplaint: e.target.value })
                    }
                    size="sm"
                    bg="gray.700"
                  />
                </FormControl>

                <Heading size="xs" mt={2}>
                  Vital Signs
                </Heading>

                <FormControl>
                  <FormLabel fontSize="sm">Heart Rate (bpm)</FormLabel>
                  <NumberInput
                    value={patientContext.heartRate || ""}
                    onChange={(_, val) => setPatientContext({ ...patientContext, heartRate: val })}
                    size="sm"
                  >
                    <NumberInputField bg="gray.700" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Blood Pressure (mmHg)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={patientContext.bloodPressureSystolic || ""}
                      onChange={(_, val) =>
                        setPatientContext({ ...patientContext, bloodPressureSystolic: val })
                      }
                      size="sm"
                    >
                      <NumberInputField bg="gray.700" placeholder="Sys" />
                    </NumberInput>
                    <Text>/</Text>
                    <NumberInput
                      value={patientContext.bloodPressureDiastolic || ""}
                      onChange={(_, val) =>
                        setPatientContext({ ...patientContext, bloodPressureDiastolic: val })
                      }
                      size="sm"
                    >
                      <NumberInputField bg="gray.700" placeholder="Dia" />
                    </NumberInput>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Respiratory Rate (bpm)</FormLabel>
                  <NumberInput
                    value={patientContext.respiratoryRate || ""}
                    onChange={(_, val) =>
                      setPatientContext({ ...patientContext, respiratoryRate: val })
                    }
                    size="sm"
                  >
                    <NumberInputField bg="gray.700" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">SpO2 (%)</FormLabel>
                  <NumberInput
                    value={patientContext.spO2 || ""}
                    onChange={(_, val) => setPatientContext({ ...patientContext, spO2: val })}
                    size="sm"
                    max={100}
                  >
                    <NumberInputField bg="gray.700" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Temperature (°F)</FormLabel>
                  <NumberInput
                    value={patientContext.temperature || ""}
                    onChange={(_, val) =>
                      setPatientContext({ ...patientContext, temperature: val })
                    }
                    size="sm"
                  >
                    <NumberInputField bg="gray.700" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Blood Glucose (mg/dL)</FormLabel>
                  <NumberInput
                    value={patientContext.bloodGlucose || ""}
                    onChange={(_, val) =>
                      setPatientContext({ ...patientContext, bloodGlucose: val })
                    }
                    size="sm"
                  >
                    <NumberInputField bg="gray.700" />
                  </NumberInput>
                </FormControl>
              </VStack>
            </Box>
          </Collapse>
        </Box>

        {/* Panel Toggle */}
        <IconButton
          aria-label="Toggle panel"
          icon={isPanelOpen ? <FaChevronLeft /> : <FaChevronRight />}
          onClick={togglePanel}
          position="absolute"
          left={isPanelOpen ? "292px" : "0"}
          top="50%"
          transform="translateY(-50%)"
          zIndex={10}
          size="sm"
          colorScheme="blue"
          transition="left 0.3s"
        />

        {/* Main Area */}
        <Flex flex={1} direction="column" overflow="hidden">
          {/* Conversation History */}
          <Box flex={1} overflowY="auto" p={6}>
            <VStack spacing={4} align="stretch">
              {conversationHistory.map((msg) => (
                <Flex
                  key={msg.id}
                  justify={msg.role === "user" ? "flex-end" : "flex-start"}
                  w="full"
                >
                  <Box
                    maxW="70%"
                    bg={msg.role === "user" ? "blue.600" : "green.600"}
                    p={4}
                    borderRadius="lg"
                  >
                    <Text whiteSpace="pre-wrap">{msg.content}</Text>
                    <Text fontSize="xs" color="whiteAlpha.700" mt={2}>
                      {msg.timestamp.toLocaleTimeString()}
                    </Text>
                  </Box>
                </Flex>
              ))}
              <div ref={conversationEndRef} />
            </VStack>
          </Box>

          {/* Input Area */}
          <Box bg="gray.800" p={6} borderTop="1px" borderColor="gray.700">
            {mode === "voice" ? (
              <Flex justify="center" align="center">
                <Button
                  onClick={recordingState === "idle" ? startRecording : stopRecording}
                  isDisabled={recordingState === "processing" || recordingState === "responding"}
                  colorScheme={recordingState === "recording" ? "red" : "blue"}
                  size="lg"
                  borderRadius="full"
                  w="120px"
                  h="120px"
                  fontSize="3xl"
                  boxShadow="xl"
                  _hover={{
                    transform: "scale(1.05)",
                  }}
                  transition="all 0.2s"
                  animation={
                    recordingState === "recording" ? "pulse 1.5s ease-in-out infinite" : undefined
                  }
                  sx={{
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.6 },
                    },
                  }}
                >
                  {recordingState === "processing" || recordingState === "responding" ? (
                    <Spinner size="xl" />
                  ) : recordingState === "recording" ? (
                    <FaStop />
                  ) : (
                    <FaMicrophone />
                  )}
                </Button>
              </Flex>
            ) : (
              <HStack spacing={3}>
                <Textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleTextSubmit();
                    }
                  }}
                  placeholder="Type your message... (Shift+Enter for new line)"
                  bg="gray.700"
                  resize="none"
                  rows={3}
                  isDisabled={recordingState !== "idle"}
                />
                <IconButton
                  aria-label="Send message"
                  icon={recordingState === "responding" ? <Spinner /> : <FaPaperPlane />}
                  onClick={handleTextSubmit}
                  colorScheme="blue"
                  size="lg"
                  isDisabled={!textInput.trim() || recordingState !== "idle"}
                />
              </HStack>
            )}

            {mode === "voice" && (
              <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
                Press Space or click to record • Esc to cancel
              </Text>
            )}
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}
